/**
 * Other — Fresh take on the Other third-party page.
 *
 * Copied from materialPivot.js with Other-specific cost types and config.
 * Key differences:
 *   1. Uses buildEtcLedger() instead of setUpUserObject()
 *   2. Every DB entry stays as an individual record
 *   3. Multi-entry EtcPopover support
 *   4. startingCodes seed tree for 20 cost type categories
 *   5. Cost_Type_Code as first drill column
 *   6. No Line_ID in drill, no description-visible detail columns
 *   7. Section grouping (LABOR / EQUIPMENT / OTHER)
 *   8. Incentive eligible panel
 *   9. Custom budgetInit/budgetDisplay for Cost_Type_Code + Phase_Code levels
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { View, Text, TextInput, StyleSheet, Pressable, Icon2, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom, selectedVersionAtom, jobUserDataAtom, phasedForecastAtom,
    jobOpenCommitmentsAtom, jobCostDetailAtom, isLiveVersionAtom, jobPhaseCodesAtom,
    jobUserDataPrevAtom, jobOpenCommitmentsPrevAtom, jobBillingsPrevAtom,
    jobCostDetailPrevAtom, selectedVersionLockedAtom, dataSourceVersionAtom, selectedWIPAtom,
    incentiveEligibleAtom, incentiveEligibleAmountAtom, incentiveCalcBreakdownAtom, incentiveRateAtom,
    projectCompleteAtom
} from '../store/company';
import {
    updateUserDataArray, deleteUserData, loadUserData,
    f, fNum, pNum, sum, cN, loadJobOpenCommitmentsCombined, loadCostHistoryCombined
} from '../db/company';
import moment from 'moment';
import { buildColumnTree, calculateColumnTotals } from '../helpers/columnTreeBuilder';
import { buildEtcLedger, getEntryLevel, rekeyEtcMapByColumns } from '../helpers/etcLedger';
import { createBudgetResolver, computePageBudget } from '../helpers/budgetResolver';
import { fieldBonusRemoved, versionStartFor } from '../helpers/fieldBonus';
import { loadResourceCodeTables, buildResourceDropdownFromDb } from '../helpers/resourceCodeDb';
import PivotSelector, { pivotLabels } from './PivotSelector';
import VarianceWeekSelector from './VarianceWeekSelector';
import { ThirdPartyRow, ThirdPartyRowCollapsable, ThirdPartyRowContext } from './ThirdPartyRow';
import { ReversedVoucherLegend } from './reversedVoucher';
import EtcPopoverHost, { PopoverBreadcrumbContext } from './EtcPopoverHost';
import { Bar } from 'react-chartjs-2';
import { SERIES, SERIES_OTHER, alpha, GRID } from '../styles/chartColors';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import AuditHistoryPanel from './auditHistoryPanel';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

// ── constants ──────────────────────────────────────────────────────────

const availablePivotColumns = ['Cost_Type_Code', 'Phase_Code', 'Discipline', 'Resource_Code', 'Supplier_Name', 'PO_Number'];

const tDescriptions = {
    // (PAGE_COST_TYPES below mirrors these keys)
    '51800': 'Field Bonus',
    '52300': 'Fuel',
    '52400': 'Repairs',
    '55100': 'Personal Meals',
    '55200': 'Team Meals/Entertain',
    '55300': 'Airfare',
    '55400': 'Lodging',
    '55900': 'Other Travel',
    '56100': 'Field Office Rent',
    '56200': 'Field Office Utility',
    '56300': 'Field Office Expense',
    '56400': 'Lab Fees & Testing',
    '56500': 'Safety & PPE',
    '56600': 'Bonds and Insurance Premiums',
    '56700': 'Permits',
    '56800': 'Insurance Claims',
    '57100': 'Taxes',
    '57200': 'Accrued Expense',
    '57300': 'Warranty Reserve',
    '57900': 'Miscellaneous',
};

const PAGE_COST_TYPES = Object.keys(tDescriptions);

const startingCodes = {
    '51800': { data: { key: '51800', breadcrumb: '^51800', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '51800' } } },
    '52300': { data: { key: '52300', breadcrumb: '^52300', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '52300' } } },
    '52400': { data: { key: '52400', breadcrumb: '^52400', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '52400' } } },
    '55100': { data: { key: '55100', breadcrumb: '^55100', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '55100' } } },
    '55200': { data: { key: '55200', breadcrumb: '^55200', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '55200' } } },
    '55300': { data: { key: '55300', breadcrumb: '^55300', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '55300' } } },
    '55400': { data: { key: '55400', breadcrumb: '^55400', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '55400' } } },
    '55900': { data: { key: '55900', breadcrumb: '^55900', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '55900' } } },
    '56100': { data: { key: '56100', breadcrumb: '^56100', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '56100' } } },
    '56200': { data: { key: '56200', breadcrumb: '^56200', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '56200' } } },
    '56300': { data: { key: '56300', breadcrumb: '^56300', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '56300' } } },
    '56400': { data: { key: '56400', breadcrumb: '^56400', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '56400' } } },
    '56500': { data: { key: '56500', breadcrumb: '^56500', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '56500' } } },
    '56600': { data: { key: '56600', breadcrumb: '^56600', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '56600' } } },
    '56700': { data: { key: '56700', breadcrumb: '^56700', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '56700' } } },
    '56800': { data: { key: '56800', breadcrumb: '^56800', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '56800' } } },
    '57100': { data: { key: '57100', breadcrumb: '^57100', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '57100' } } },
    '57200': { data: { key: '57200', breadcrumb: '^57200', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '57200' } } },
    '57300': { data: { key: '57300', breadcrumb: '^57300', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '57300' } } },
    '57900': { data: { key: '57900', breadcrumb: '^57900', indent: 0, path: 'Cost_Type_Code', detail: { Cost_Type_Code: '57900' } } },
};

const indentColors = {
    1: '#e8edf2',
    2: '#eef1f6',
    3: '#f3f5f9',
    4: '#f8f9fb',
};

const coCodeType = [
    '51100','51200','51300','51350','51400','51500','51600','51700','51800',
    '52100','52200','52300','52400',
    '53100','53200','53300','53400',
    '54100','54200','54300',
    '55100','55200','55300','55400','55900',
    '56100','56200','56300','56400','56500','56600','56700','56800',
    '57100','57200','57300','57900',
];

// ── Collect descendant entries from a tree node's children ──
const collectChildEntries = (node, entriesByBreadcrumb) => {
    const childEntries = [];
    const walk = (n) => {
        if (!n?.rows || n.rows === false) return;
        Object.values(n.rows).forEach(child => {
            const bc = child.data?.breadcrumb;
            if (bc && entriesByBreadcrumb[bc]) {
                childEntries.push(...entriesByBreadcrumb[bc]);
            }
            walk(child);
        });
    };
    walk(node);
    return childEntries;
};

// ── PivotRowCollapsable ──
const PivotRowCollapsable = React.memo(({ data }) => {
    const ctx = React.useContext(ThirdPartyRowContext);
    const popoverBreadcrumb = React.useContext(PopoverBreadcrumbContext);
    const { entriesByBreadcrumb = {}, setPopoverBreadcrumb, pivotPopoverProps, onOpenFuelChart, descriptionVisible } = ctx;
    const isFuelRow = data?.data?.breadcrumb?.startsWith('^52300') && !data?.data?.breadcrumb?.includes('^52300^');
    const allEntries = data?.data?.breadcrumb ? (entriesByBreadcrumb[data.data.breadcrumb] || []) : [];
    const entries = allEntries.filter(e => e.amount !== 0 || e.description || e.notes);
    const allChildEntries = useMemo(() => collectChildEntries(data, entriesByBreadcrumb), [data, entriesByBreadcrumb]);
    const childEntries = allChildEntries.filter(e => e.amount !== 0 || e.description || e.notes);
    const totalCount = entries.length + childEntries.length;
    const isOpen = popoverBreadcrumb === data?.data?.breadcrumb;
    const hasBreadcrumb = !!data?.data?.breadcrumb;

    return (
        <View style={{ position: 'relative' }}>
            <ThirdPartyRowCollapsable data={data} />
            {isFuelRow && (
                <Pressable
                    onPress={() => onOpenFuelChart?.()}
                    style={{ position: 'absolute', top: 3, zIndex: 5, cursor: 'pointer', left: (descriptionVisible ? 1000 : 0) + (totalCount >= 2 ? 960 : 885) }}
                >
                    <View style={{ backgroundColor: '#f59e0b', borderRadius: 3, padding: '3px 5px', marginLeft: 3, boxShadow: '0 1px 2px rgba(0,0,0,0.12)', transition: 'all 0.15s ease' }}>
                        <Icon3 name="MdBarChart" size={13} color="white" />
                    </View>
                </Pressable>
            )}
            {hasBreadcrumb && (
                <>
                    {totalCount >= 2 && (
                        <Pressable
                            onPress={() => setPopoverBreadcrumb(isOpen ? null : data.data.breadcrumb)}
                            style={{
                                position: 'absolute', top: 3, zIndex: 5, cursor: 'pointer',
                                left: descriptionVisible ? 1885 : 885,
                            }}
                        >
                            <View style={{
                                backgroundColor: isOpen ? '#1d4ed8' : '#3b82f6',
                                borderRadius: 3,
                                padding: '1px 5px', marginLeft: 3, whiteSpace: 'nowrap', lineHeight: 1.2,
                                boxShadow: isOpen ? '0 0 0 2px rgba(59,130,246,0.3)' : '0 1px 2px rgba(0,0,0,0.12)',
                                transition: 'all 0.15s ease',
                            }}>
                                <Text style={{ fontSize: 8, fontWeight: '700', color: 'white', whiteSpace: 'nowrap' }}>{totalCount} val.</Text>
                            </View>
                        </Pressable>
                    )}
                </>
            )}
        </View>
    );
});

// ── Fuel ETC Warning with projection chart ─────────────────────────────

const FuelETCWarningModal = ({ open, onClose, jobCostDetail, userData, openCommitments }) => {
    const [projectComplete] = useAtom(projectCompleteAtom)
    const [versions] = useAtom(dataSourceVersionAtom)
    const [selectedVersion] = useAtom(selectedVersionAtom)
    const popupRef = useRef(null)

    useEffect(() => {
        if (!open) return
        const handler = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) onClose?.()
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open, onClose])

    const analysis = useMemo(() => {
        const fuelRows = Array.isArray(jobCostDetail) ? jobCostDetail.filter(r => r.Cost_Type_Code === '52300') : []
        if (!fuelRows.length) return null

        const etcRows = Array.isArray(userData) ? userData.filter(d =>
            d?.Cost_Type === '52300' && d?.Forecast_Tab === 'Other' && d?.Value_Amount
        ) : []
        const fuelEtc = etcRows.reduce((s, d) => s + pNum(d?.Value_Amount), 0)

        const fuelOC = Array.isArray(openCommitments) ? openCommitments.filter(r => r.Cost_Type_Code === '52300').reduce((s, r) => s + pNum(r.PO_True_Open_Amount), 0) : 0

        const totalActual = fuelRows.reduce((s, r) => s + pNum(r.Job_Cost), 0)

        const weekMap = {}
        fuelRows.forEach(r => {
            const d = moment(r.GL_Date || r.Transaction_Date)
            if (!d.isValid()) return
            const wk = d.clone().startOf('isoWeek').format('YYYY-MM-DD')
            weekMap[wk] = (weekMap[wk] || 0) + pNum(r.Job_Cost)
        })
        const actualWeeks = Object.keys(weekMap).sort()
        if (!actualWeeks.length) return null

        const lastActualWeek = moment(actualWeeks[actualWeeks.length - 1])
        const projEnd = projectComplete ? moment(projectComplete) : lastActualWeek.clone().add(12, 'weeks')
        const lowData = actualWeeks.length < 3

        const remainingWeeks = []
        let cursor = lastActualWeek.clone().add(1, 'week').startOf('isoWeek')
        while (cursor.isSameOrBefore(projEnd, 'week')) {
            remainingWeeks.push(cursor.format('YYYY-MM-DD'))
            cursor.add(1, 'week')
        }

        const avgWeeklySpend = totalActual / actualWeeks.length

        // Use recent weeks (last 3) to set projection starting point
        const recentN = Math.min(3, actualWeeks.length)
        const recentWeeks = actualWeeks.slice(-recentN)
        const recentAvg = recentWeeks.reduce((s, w) => s + (weekMap[w] || 0), 0) / recentN

        const totalRemainingWeeks = remainingWeeks.length
        if (totalRemainingWeeks === 0) return { actualWeeks, remainingWeeks: [], allWeeks: actualWeeks, weekMap, projectedMap: {}, totalActual, projectedTotal: 0, fuelEtc, fuelOC, etcPlusOC: fuelEtc + fuelOC, etcPerWeek: 0, overEtcPct: 0, isWarning: false, avgWeeklySpend, recentAvg, lowData }

        // Bell curve taper: start at recent average, z 0→2 for gentler decline
        const bellWeights = []
        for (let i = 0; i < totalRemainingWeeks; i++) {
            const z = (i / totalRemainingWeeks) * 2
            bellWeights.push(Math.exp(-0.5 * z * z))
        }
        const scale = recentAvg / (bellWeights[0] || 1)
        const projectedMap = {}
        let projectedTotal = 0
        remainingWeeks.forEach((wk, i) => {
            const val = bellWeights[i] * scale
            projectedMap[wk] = val
            projectedTotal += val
        })

        // Compare projected remaining spend vs (ETC + Open Commitments)
        const etcPlusOC = fuelEtc + fuelOC
        const overEtcPct = etcPlusOC > 0 ? ((projectedTotal - etcPlusOC) / etcPlusOC) * 100 : 0
        const isWarning = etcPlusOC > 0 && overEtcPct >= 20
        const allWeeks = [...new Set([...actualWeeks, ...remainingWeeks])].sort()
        // ETC line: spread (ETC + OC) evenly across only the remaining weeks
        const etcPerWeek = remainingWeeks.length > 0 ? etcPlusOC / remainingWeeks.length : 0

        return { actualWeeks, remainingWeeks, allWeeks, weekMap, projectedMap, totalActual, projectedTotal, fuelEtc, fuelOC, etcPlusOC, etcPerWeek, overEtcPct, isWarning, avgWeeklySpend, recentAvg, lowData }
    }, [jobCostDetail, userData, projectComplete, versions, selectedVersion])

    if (!open || !analysis) return null

    const { allWeeks, weekMap, projectedMap, totalActual, projectedTotal, fuelEtc, fuelOC, etcPlusOC, etcPerWeek, overEtcPct, isWarning, avgWeeklySpend, recentAvg, lowData, remainingWeeks } = analysis

    const chartData = {
        labels: allWeeks.map(w => moment(w).format('M/D')),
        datasets: [
            { label: 'Actual Fuel Spend', data: allWeeks.map(w => weekMap[w] || 0), backgroundColor: alpha(SERIES[0], 0.8), borderColor: SERIES[0], borderWidth: 1 },
            { label: 'Projected Fuel Spend', data: allWeeks.map(w => projectedMap[w] || 0), backgroundColor: alpha(SERIES[1], 0.55), borderColor: SERIES[1], borderWidth: 1, borderDash: [4, 2] },
            { label: '(ETC + Open) / Week', data: allWeeks.map(w => remainingWeeks.includes(w) ? etcPerWeek : 0), type: 'line', borderColor: SERIES[2], borderWidth: 2, borderDash: [6, 3], pointRadius: 0, fill: false },
        ],
    }

    const chartOptions = {
        responsive: true, maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top', labels: { usePointStyle: true, padding: 10, font: { size: 11 } } },
            tooltip: { callbacks: { label: ctx => ctx.dataset.label + ': $' + fNum(Math.round(ctx.parsed.y)) } },
        },
        scales: {
            x: { ticks: { font: { size: 9 }, maxRotation: 45 }, grid: { display: false } },
            y: { beginAtZero: true, ticks: { callback: v => '$' + fNum(v), font: { size: 10 } }, grid: { color: GRID } },
        },
    }

    return (
        <View style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 9999, justifyContent: 'center', alignItems: 'center' }}>
            <div ref={popupRef} style={{ backgroundColor: '#fff', borderRadius: 12, width: '85%', maxWidth: 900, maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)', padding: 24 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <View>
                        <Text style={{ fontSize: 18, fontWeight: '700', color: '#0f172a' }}>Fuel Spend Projection (52300)</Text>
                        <Text style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Actual weekly spend + bell curve projection to project end</Text>
                    </View>
                    <Pressable onPress={onClose} style={{ padding: 8, cursor: 'pointer' }}>
                        <Text style={{ fontSize: 20, color: '#94a3b8', fontWeight: '700' }}>✕</Text>
                    </Pressable>
                </View>

                {lowData && (
                    <View style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 12px', marginBottom: 12, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                        <Text style={{ fontSize: 13 }}>⚠️</Text>
                        <Text style={{ fontSize: 11, color: '#92400e', fontWeight: '600' }}>Low data — only {analysis.actualWeeks.length} week(s) of fuel history. Projection may not be reliable.</Text>
                    </View>
                )}

                <View style={{ flexDirection: 'row', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                    <View style={{ flex: 1, minWidth: 120, backgroundColor: '#f0f9ff', borderRadius: 8, padding: 12, border: '1px solid #bae6fd' }}>
                        <Text style={{ fontSize: 10, color: '#0369a1', fontWeight: '600', textTransform: 'uppercase' }}>Actual Fuel</Text>
                        <Text style={{ fontSize: 20, fontWeight: '700', color: '#0c4a6e', marginTop: 4 }}>${fNum(Math.round(totalActual))}</Text>
                    </View>
                    <View style={{ flex: 1, minWidth: 120, backgroundColor: '#fffbeb', borderRadius: 8, padding: 12, border: '1px solid #fde68a' }}>
                        <Text style={{ fontSize: 10, color: '#92400e', fontWeight: '600', textTransform: 'uppercase' }}>Projected Remaining</Text>
                        <Text style={{ fontSize: 20, fontWeight: '700', color: '#78350f', marginTop: 4 }}>${fNum(Math.round(projectedTotal))}</Text>
                    </View>
                    <View style={{ flex: 1, minWidth: 120, backgroundColor: '#faf5ff', borderRadius: 8, padding: 12, border: '1px solid #e9d5ff' }}>
                        <Text style={{ fontSize: 10, color: '#6b21a8', fontWeight: '600', textTransform: 'uppercase' }}>ETC + Open</Text>
                        <Text style={{ fontSize: 20, fontWeight: '700', color: '#581c87', marginTop: 4 }}>${fNum(Math.round(etcPlusOC))}</Text>
                        <Text style={{ fontSize: 9, color: '#a78bfa', marginTop: 2 }}>ETC ${fNum(Math.round(fuelEtc))} + OC ${fNum(Math.round(fuelOC))}</Text>
                    </View>
                    <View style={{ flex: 1, minWidth: 120, backgroundColor: isWarning ? '#fef2f2' : '#f8fafc', borderRadius: 8, padding: 12, border: '1px solid ' + (isWarning ? '#fecaca' : '#e2e8f0') }}>
                        <Text style={{ fontSize: 10, color: isWarning ? '#991b1b' : '#475569', fontWeight: '600', textTransform: 'uppercase' }}>Projected vs (ETC+Open)</Text>
                        <Text style={{ fontSize: 20, fontWeight: '700', color: isWarning ? '#dc2626' : '#334155', marginTop: 4 }}>{overEtcPct >= 0 ? '+' : ''}{Math.round(overEtcPct)}%</Text>
                    </View>
                </View>

                <div style={{ height: 380, marginBottom: 12 }}>
                    <Bar data={chartData} options={chartOptions} />
                </div>

                <p style={{ fontSize: 11, color: '#94a3b8', lineHeight: '16px', margin: '4px 0 0 0', padding: 0 }}>
                    Avg spend: ${fNum(Math.round(avgWeeklySpend))}/wk overall, ${fNum(Math.round(recentAvg))}/wk recent (last 3). Projection starts from recent avg with bell curve taper to completion. Red dashed line = (ETC + Open) spread across remaining weeks.
                    {isWarning ? ' Projected remaining exceeds ETC by 20%+ — review fuel estimate.' : ''}
                </p>
            </div>
        </View>
    )
}

// ── component ──────────────────────────────────────────────────────────

const Other = () => {
    // ── atoms ──
    const [userData, setUserData] = useAtom(jobUserDataAtom);
    const [jobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [jobCostHistory] = useAtom(jobCostDetailAtom);
    const [isLiveVersion] = useAtom(isLiveVersionAtom);
    const [userDataPrev] = useAtom(jobUserDataPrevAtom);
    const [jobOpenCommitmentsPrev] = useAtom(jobOpenCommitmentsPrevAtom);
    const [jobCostHistoryPrev] = useAtom(jobCostDetailPrevAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [phased] = useAtom(phasedForecastAtom);
    const [phaseDescriptions] = useAtom(jobPhaseCodesAtom);
    const [versions] = useAtom(dataSourceVersionAtom);
    const [selectedWIP] = useAtom(selectedWIPAtom);
    const [incentiveEligible] = useAtom(incentiveEligibleAtom);
    const [incentiveEligibleAmount] = useAtom(incentiveEligibleAmountAtom);
    const [incentiveCalcBreakdown] = useAtom(incentiveCalcBreakdownAtom);
    const [incentiveRate, setIncentiveRate] = useAtom(incentiveRateAtom);

    // Field Bonus (51800) is retired for versions June 18, 2026 forward — hide
    // the section and drop it from every total for those versions only.
    const hideFieldBonus = fieldBonusRemoved(versionStartFor(versions, selectedVersion, selectedWIP));
    const pageDescriptions = useMemo(() => {
        if (!hideFieldBonus) return tDescriptions;
        const { '51800': _omit, ...rest } = tDescriptions;
        return rest;
    }, [hideFieldBonus]);
    const pageStartingCodes = useMemo(() => {
        if (!hideFieldBonus) return startingCodes;
        const { '51800': _omit, ...rest } = startingCodes;
        return rest;
    }, [hideFieldBonus]);

    // ── local state ──
    const [objectMap, setObjectMap] = useState({});
    const [objectMapPrev, setObjectMapPrev] = useState({});
    const [openCommitments, setOpenCommitments] = useState([]);
    const [costHistory, setCostHistory] = useState([]);
    const [openCommitmentsPrev, setOpenCommitmentsPrev] = useState([]);
    const [costHistoryPrev, setCostHistoryPrev] = useState([]);
    const [expandedBreadcrumbs, setExpandedBreadcrumbs] = useState({});
    const [phaseDayZero, setPhaseDayZero] = useState({});
    const [phaseCCDayZero, setPhaseCCDayZero] = useState({});
    const [costTypeBudget, setCostTypeBudget] = useState({});
    const [budgetByCtPhase, setBudgetByCtPhase] = useState({});
    const [budgetByPhaseCt, setBudgetByPhaseCt] = useState({});
    const [dayZeroChangeOrderPhases, setDayZeroChangeOrderPhases] = useState([]);
    const titleDescriptions = pageDescriptions;
    const [dbResourceCodes, setDbResourceCodes] = useState({ byCode: {}, codesByCostType: {}, mappingByCode: {} });
    const [descriptionVisible, setDescriptionVisible] = useState(false);
    const [collapseAll, setCollapseAll] = useState(false);
    const [collapseNumber, setCollapseNumber] = useState(0);
    const [activeCostTypes, setActiveCostTypes] = useState(() => {
        const init = {}; Object.keys(tDescriptions).forEach(k => init[k] = true); return init;
    });
    const [sortedUserData, setSortedUserData] = useState([]);
    const [deletingRows, setDeletingRows] = useState(new Set());
    const [isExporting, setIsExporting] = useState(false);
    const [showFuelChart, setShowFuelChart] = useState(false);
    const focusedId = useRef(null);

    const [totalCurrentBudget, setTotalCurrentBudget] = useState(0);
    const [actualTotalTotal, setActualTotalTotal] = useState(0);
    const [openTotalTotal, setOpenTotalTotal] = useState(0);
    const [etcTotalTotal, setEtcTotalTotal] = useState(0);
    const [totalTotalTotal, setTotalTotalTotal] = useState(0);
    const [varianceBudgetTotal, setVarianceBudgetTotal] = useState(0);

    const [varianceWeeks, setVarianceWeeks] = useState([]);
    const [weekTotalMaps, setWeekTotalMaps] = useState({});
    const [weekLoading, setWeekLoading] = useState({});
    const [weekDisplayMode, setWeekDisplayMode] = useState({});
    const [prevGrandTotal, setPrevGrandTotal] = useState(0);

    const [popoverBreadcrumb, setPopoverBreadcrumb] = useState(null);
    const [entriesByBreadcrumb, setEntriesByBreadcrumb] = useState({});
    const [rebuilding, setRebuilding] = useState(false);

    const ledgerRef = useRef({ userEtcMap: {}, entriesByBreadcrumb: {} });

    // ── drill columns (Cost_Type_Code first) ──
    const [drillColumns, setDrillColumns] = useState(
        phased === 'phased'
            ? ['Cost_Type_Code', 'Phase_Code', 'Supplier_Name', 'PO_Number']
            : ['Cost_Type_Code', 'Supplier_Name', 'PO_Number']
    );
    useEffect(() => {
        setDrillColumns(phased === 'phased'
            ? ['Cost_Type_Code', 'Phase_Code', 'Supplier_Name', 'PO_Number']
            : ['Cost_Type_Code', 'Supplier_Name', 'PO_Number']);
    }, [phased]);

    const path = useMemo(() => [...drillColumns, 'PO_Detail'], [drillColumns]);

    // ── styles (cached) ──
    const _sc = useRef(new Map());
    const _createStyles = (w = null) => StyleSheet.create({
        edit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: w || 100, padding: 2, paddingLeft: 8, textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        notEdit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: w || 100, padding: 2, maxWidth: w || 100, textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        firstNotEdit: { borderBottom: '1px solid rgba(15,23,42,0.06)', fontSize: 12, width: '100%', padding: 2, maxWidth: '100%', textAlign: 'right', height: 28, lineHeight: '28px', fontVariantNumeric: 'tabular-nums', transition: 'background-color 0.15s ease' },
        title: { width: w || 100, alignItems: 'center', fontSize: 10, fontWeight: '600', padding: '6px 4px', textAlign: 'right', justifyContent: 'flex-end', borderBottom: '1px solid #e5e7eb', backgroundColor: 'transparent', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.6 },
    });
    const s = (w = null) => {
        if (_sc.current.has(w)) return _sc.current.get(w);
        const r = _createStyles(w); _sc.current.set(w, r); return r;
    };

    // ── OC / CH preprocessing ──
    useEffect(() => {
        const oC = [];
        (jobOpenCommitments || []).forEach(e => {
            const d = { ...e };
            if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
            if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
            d.OC = true;
            d.Supplier_Name = d.Supplier_AB_Name;
            d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            d.Line_ID = d.PO_Line_Number || '';
            oC.push(d);
        });
        setOpenCommitments(oC);
    }, [jobOpenCommitments]);

    useEffect(() => {
        const jCH = [];
        (jobCostHistory || []).forEach(d => {
            const c = { ...d };
            c.Supplier_Name = d.Supplier_Name ? d.Supplier_Name : d.Is_Credit_Card_Transaction ? 'Credit Card' : 'Unknown Vendor';
            c.CH = true;
            c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            jCH.push(c);
        });
        setCostHistory(jCH);
    }, [jobCostHistory]);

    useEffect(() => {
        const oC = [];
        (jobOpenCommitmentsPrev || []).forEach(e => {
            const d = { ...e };
            if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
            if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
            d.OC = true;
            d.Supplier_Name = d.Supplier_AB_Name;
            d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            d.Line_ID = d.PO_Line_Number || '';
            oC.push(d);
        });
        setOpenCommitmentsPrev(oC);
    }, [jobOpenCommitmentsPrev]);

    useEffect(() => {
        const jCH = [];
        (jobCostHistoryPrev || []).forEach(d => {
            const c = { ...d };
            c.CH = true;
            c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number;
            jCH.push(c);
        });
        setCostHistoryPrev(jCH);
    }, [jobCostHistoryPrev]);

    const disciplineMap = useMemo(() => {
        const map = {};
        (userData || []).forEach(d => {
            if (d?.Forecast_Tab === 'Productivity Tracker' && d?.Forecast_Field_Name === 'discipline') {
                map[d?.Forecast_Row_ID] = d?.Forecast_Value;
            }
        });
        return map;
    }, [userData]);

    // ── main data processing pipeline ──
    const processDebounceRef = useRef(null);

    useEffect(() => {
        setRebuilding(true);
        if (processDebounceRef.current) clearTimeout(processDebounceRef.current);
        processDebounceRef.current = setTimeout(() => {
            if (!userData || !phaseDescriptions) { setRebuilding(false); return; }

            const ledger = buildEtcLedger(userData, 'Other', drillColumns, disciplineMap);
            ledgerRef.current = ledger;
            setEntriesByBreadcrumb(ledger.entriesByBreadcrumb);
            setPhaseDayZero(ledger.dayZeroBudget);
            setPhaseCCDayZero(ledger.budgetByPhaseCt || {});
            setCostTypeBudget(ledger.costTypeBudget || {});
            setBudgetByCtPhase(ledger.budgetByCtPhase || {});
            setBudgetByPhaseCt(ledger.budgetByPhaseCt || {});
            // Page budget = only this page's cost types — never ledger.totalBudget,
            // which spans the whole job (labor, equipment, subs, ...).
            const pageCts = hideFieldBonus ? PAGE_COST_TYPES.filter(ct => ct !== '51800') : PAGE_COST_TYPES;
            const pageBudget = computePageBudget(ledger.costTypeBudget, pageCts);
            setTotalCurrentBudget(fNum(pageBudget));
            setDayZeroChangeOrderPhases(Object.values(ledger.costCodeSeeds));

            const userRows = ledger.allEntries
                .filter(e => e.amount !== 0 || e.notes || e.description)
                .map(e => {
                    const d = e.depth || 1;
                    return {
                        ...e._row,
                        rowId: e._row?.Forecast_Row_ID,
                        Forecast_Row_ID: e.breadcrumb,
                        Forecast_Tab: e._row?.Forecast_Tab || 'Other',
                        Cost_Type_Code: e.Cost_Type_Code,
                        Phase_Code: d >= 2 ? e.Phase_Code : '',
                        Supplier_Name: d >= 2 ? e.Supplier_Name : '',
                        PO_Number: d >= 3 ? e.PO_Number : '',
                        Value_Amount: e.amount,
                    };
                });

            const jOC = openCommitments.length ? openCommitments : [];
            const jCH = costHistory.length ? costHistory : [];
            const dzPhases = Object.values(ledger.costCodeSeeds);
            const d = jOC.length && jCH.length ? [...jOC, ...jCH] : jOC.length ? jOC : jCH;
            const d2 = dzPhases.length && d.length ? [...d, ...dzPhases] : d.length ? d : dzPhases;
            const threeDataSources = d2.length && userRows.length ? [...d2, ...userRows] : d2.length ? d2 : userRows;

            const anyCtActive = Object.values(activeCostTypes).some(Boolean);
            const filtered = (anyCtActive
                ? threeDataSources.filter(r => !r.Cost_Type_Code || (pageDescriptions[r.Cost_Type_Code] && activeCostTypes[r.Cost_Type_Code]))
                : threeDataSources
            ).filter(r => !(hideFieldBonus && r.Cost_Type_Code === '51800'));

            // Build Vendor+PO → Resource_Code map from cost history / OC so legacy
            // user ETC entries saved without Resource_Code can inherit one.
            const vendorPoToResource = {};
            [...jOC, ...jCH].forEach(r => {
                const vendor = r.Supplier_Name || r.Supplier_AB_Name || '';
                const po = r.PO_Number || '';
                const code = r.Purchasing_Reporting_Code_4_Description || r.Purchasing_Reporting_Code_1_Description || '';
                if (vendor && po && code) {
                    const key = vendor + '^' + po;
                    if (!vendorPoToResource[key]) vendorPoToResource[key] = code;
                }
            });
            filtered.forEach(row => {
                if (!row.Discipline) row.Discipline = disciplineMap[row.Phase_Code] || '';
                // Preserve Resource_Code if already set (e.g. user ETC rows carry it from the ledger)
                if (!row.Resource_Code) row.Resource_Code = row.Purchasing_Reporting_Code_4_Description || row.Purchasing_Reporting_Code_1_Description || '';
                // Legacy fallback: infer from cost history via Vendor+PO
                if (!row.Resource_Code && row.Supplier_Name && row.PO_Number) {
                    row.Resource_Code = vendorPoToResource[row.Supplier_Name + '^' + row.PO_Number] || '';
                }
            });

            const drillPath = path.filter(p => p !== 'PO_Detail');
            // Re-key userEtcMap so tree's fresh breadcrumb (with enriched Resource_Code) matches
            const rekeyedEtcMap = rekeyEtcMapByColumns(filtered, ledger.userEtcMap, drillPath);
            let tree = buildColumnTree(filtered, drillPath, rekeyedEtcMap, expandedBreadcrumbs, ledger.dayZeroBudget, { seedTree: pageStartingCodes });
            if (drillPath[0] === 'Resource_Code' && tree['']) {
                const n = tree[''];
                n.data.key = 'Unassigned';
                tree['Unassigned'] = n;
                delete tree[''];
            }

            enrichTreeWithEntries(tree, ledger.entriesByBreadcrumb);

            const budgetResolver = createBudgetResolver({
                dayZeroBudget: ledger.dayZeroBudget,
                costTypeBudget: ledger.costTypeBudget,
                budgetByPhaseCt: ledger.budgetByPhaseCt,
                budgetByCtPhase: ledger.budgetByCtPhase,
                budgetByResource: ledger.budgetByResource,
                budgetByResourceCt: ledger.budgetByResourceCt,
                drillColumns: drillPath,
                pageCostTypes: pageCts,
            });
            tree = calculateAllTotals(tree, pageBudget, budgetResolver);
            setObjectMap(tree);

            const sorted = [...userData]
                .filter(row => row.Forecast_Tab === 'Other' && row.Value_Amount)
                .sort((a, b) => (a.Forecast_User_Data_GUID + '').localeCompare(b.Forecast_User_Data_GUID + ''));
            setSortedUserData(sorted);
            setRebuilding(false);

        }, 100);
    }, [userData, openCommitments, costHistory, drillColumns, activeCostTypes, phaseDescriptions, disciplineMap, hideFieldBonus, pageDescriptions, pageStartingCodes]);

    const enrichTreeWithEntries = (tree, entriesByBC) => {
        const walk = (node) => {
            if (!node || !node.data) return;
            node.data.indent = (node.data.indent || 0) * 2;
            const bc = node.data.breadcrumb;
            if (bc && entriesByBC[bc]) {
                node.data.etcEntries = entriesByBC[bc];
                const entries = entriesByBC[bc];
                if (entries.length > 0) {
                    const best = entries.find(e => e.description) || entries.find(e => e.notes) || entries[0];
                    if (best.description) node.data.description = best.description;
                    if (best.notes) node.data.notes = best.notes;
                    if (best.title) node.data.title = best.title;
                    if (node.data.detail) {
                        node.data.detail = {
                            ...node.data.detail,
                            Description: best.description || node.data.detail.Description || '',
                            Notes: best.notes || node.data.detail.Notes || '',
                            Row_Label: best.title || node.data.detail.Row_Label || '',
                        };
                        if (best.guid && !node.data.detail.Forecast_User_Data_GUID) {
                            node.data.detail = {
                                ...node.data.detail,
                                Forecast_User_Data_GUID: best.guid,
                                Forecast_Row_ID: best._row?.Forecast_Row_ID || node.data.detail.Forecast_Row_ID,
                            };
                        }
                    }
                }
            }
            if (node.rows && node.rows !== false) {
                Object.values(node.rows).forEach(walk);
            }
        };
        Object.values(tree).forEach(walk);
    };

    // Budget lookup function — resolves budget for any node based on drill order
    const makeBudgetLookup = (dz, ctBudget, ctPhase, phaseCt) => (node) => {
        const p = node?.data?.path;
        const k = node?.data?.key;
        const parentCt = node?.data?.detail?.Cost_Type_Code;
        const parentPhase = node?.data?.detail?.Phase_Code;

        if (p === 'Cost_Type_Code') {
            // If cost type has a parent phase, use the intersection budget
            if (parentPhase && phaseCt?.[parentPhase]?.[k] != null) return phaseCt[parentPhase][k];
            // Top-level cost type: use cost-type aggregate
            return ctBudget?.[k] ?? '';
        }
        if (p === 'Phase_Code') {
            // If phase has a parent cost type, use the intersection budget
            if (parentCt && ctPhase?.[parentCt]?.[k] != null) return ctPhase[parentCt][k];
            // Top-level phase: use phase aggregate
            return dz?.[k] ?? '';
        }
        return '';
    };

    const calculateAllTotals = (tree, freshBudget = null, budgetLookup = null) => {
        let actualTT = 0, openTT = 0, etcTT = 0, totalTT = 0, varBudgetTT = 0;
        const lookup = budgetLookup || makeBudgetLookup(phaseDayZero, costTypeBudget, budgetByCtPhase, budgetByPhaseCt);
        Object.values(tree).forEach(costType => {
            calculateColumnTotals(costType, lookup);
            actualTT += pNum(costType.data?.actualTotal);
            openTT += pNum(costType.data?.openTotal);
            etcTT += pNum(costType.data?.etcTotal);
            totalTT += pNum(costType.data?.totalTotal);
            varBudgetTT += pNum(costType.data?.varianceBudget);
        });
        setActualTotalTotal(fNum(actualTT));
        setOpenTotalTotal(fNum(openTT));
        setEtcTotalTotal(fNum(etcTT));
        setTotalTotalTotal(fNum(totalTT));
        const budget = freshBudget != null ? freshBudget : pNum(totalCurrentBudget);
        setVarianceBudgetTotal(fNum(pNum(budget) - pNum(totalTT)));
        return tree;
    };

    useEffect(() => {
        if (selectedJob && selectedVersion) {
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion, live: isLiveVersion }).then(data => {
                setUserData(data?.data);
            });
        }
    }, [selectedJob, selectedVersion, phased]);

    // ── Load admin-managed Resource Code tables (version-scoped, BU_ID='all') ──
    useEffect(() => {
        if (!selectedVersion) return;
        loadResourceCodeTables({ forecastGuid: selectedVersion }).then(setDbResourceCodes);
    }, [selectedVersion]);

    const getWeekLabel = (guid) => {
        if (selectedWIP && guid === selectedWIP.Version_GUID) return 'WIP ' + moment(selectedWIP.Version_Start_DateTime).format('MMM D');
        if (versions?.length) {
            const v = versions.find(v => v.Version_GUID === guid);
            if (v) return moment(v.Version_Start_DateTime).format('MMM D');
        }
        return '';
    };

    useEffect(() => {
        if (!varianceWeeks.length || !selectedJob) return;
        const otherWeeks = varianceWeeks.filter(g => g !== selectedWIP?.Version_GUID);

        const loadWeekVersion = async (guid) => {
            setWeekLoading(prev => ({ ...prev, [guid]: true }));
            const [udRes, ocRes, chRes] = await Promise.all([
                loadUserData({ id: selectedJob, forecastGuid: guid }),
                loadJobOpenCommitmentsCombined({ id: selectedJob, versionGuid: guid }),
                loadCostHistoryCombined({ id: selectedJob, versionGuid: guid }),
            ]);
            const weekUD = udRes?.data || [];
            const weekOC = (ocRes?.data || []).map(e => {
                const d = { ...e };
                if (!d.PO_Number || d.PO_Number === 'Unknown' || d.PO_Number === '') d.PO_Number = 'Unknown PO';
                if (!d.Supplier_AB_Name || d.Supplier_AB_Name === 'Unknown' || d.Supplier_AB_Name === '') d.Supplier_AB_Name = 'Unknown Vendor';
                d.OC = true; d.Supplier_Name = d.Supplier_AB_Name; d.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number; d.Line_ID = d.PO_Line_Number || '';
                return d;
            });
            const weekCH = (chRes?.data || []).map(d => {
                const c = { ...d };
                c.Supplier_Name = d.Supplier_Name ? d.Supplier_Name : d.Is_Credit_Card_Transaction ? 'Credit Card' : 'Unknown Vendor';
                c.CH = true; c.PO_Number = d.PO_Number ? pNum(d.PO_Number) : d.PO_Number; return c;
            });

            const weekLedger = buildEtcLedger(weekUD, 'Other', drillColumns, disciplineMap);
            const weekUserRows = weekLedger.allEntries.filter(e => e.amount !== 0).map(e => ({ ...e._row, Cost_Type_Code: e.Cost_Type_Code, Phase_Code: e.Phase_Code, Supplier_Name: e.Supplier_Name, PO_Number: e.PO_Number }));
            const combined = [...weekOC, ...weekCH, ...weekUserRows];
            // Filter to page cost types (match current tree filtering); Field Bonus
            // drops out for comparison weeks past its own version's cutoff.
            const weekHideFB = fieldBonusRemoved(versionStartFor(versions, guid, selectedWIP));
            const filtered = combined.filter(r => !r.Cost_Type_Code || (tDescriptions[r.Cost_Type_Code] && !(weekHideFB && r.Cost_Type_Code === '51800')));
            const drillPath = path.filter(p => p !== 'PO_Detail');
            const weekSeed = weekHideFB ? (({ '51800': _omit, ...rest }) => rest)(startingCodes) : startingCodes;
            const weekTree = buildColumnTree(filtered, drillPath, weekLedger.userEtcMap, {}, weekLedger.dayZeroBudget, { seedTree: weekSeed });

            let totalTT = 0;
            const etcMap = {};
            Object.values(weekTree).forEach(node => {
                calculateColumnTotals(node, weekLedger.dayZeroBudget);
                totalTT += pNum(node.data?.totalTotal);
            });
            const walkMap = (obj) => {
                Object.values(obj).forEach(row => {
                    etcMap[row.data?.breadcrumb] = isNaN(row.data?.totalTotal) ? 0 : pNum(row.data?.totalTotal);
                    if (row.rows && row.rows !== false) walkMap(row.rows);
                });
            };
            walkMap(weekTree);
            return { map: etcMap, grandTotal: totalTT };
        };

        if (selectedWIP && varianceWeeks.includes(selectedWIP.Version_GUID)) {
            const prevOC = openCommitmentsPrev.length ? openCommitmentsPrev : [];
            const prevCH = costHistoryPrev.length ? costHistoryPrev : [];
            if (userDataPrev) {
                const prevLedger = buildEtcLedger(userDataPrev, 'Other', drillColumns, disciplineMap);
                const prevUserRows = prevLedger.allEntries.filter(e => e.amount !== 0).map(e => ({ ...e._row, Cost_Type_Code: e.Cost_Type_Code, Phase_Code: e.Phase_Code, Supplier_Name: e.Supplier_Name, PO_Number: e.PO_Number }));
                const combined = [...prevOC, ...prevCH, ...prevUserRows];
                // Filter to page cost types (match current tree filtering); WIP is
                // always current, so Field Bonus is past its cutoff here.
                const wipHideFB = fieldBonusRemoved(selectedWIP?.Version_Start_DateTime);
                const filtered = combined.filter(r => !r.Cost_Type_Code || (tDescriptions[r.Cost_Type_Code] && !(wipHideFB && r.Cost_Type_Code === '51800')));
                const drillPath = path.filter(p => p !== 'PO_Detail');
                const wipSeed = wipHideFB ? (({ '51800': _omit, ...rest }) => rest)(startingCodes) : startingCodes;
                const prevTree = buildColumnTree(filtered, drillPath, prevLedger.userEtcMap, {}, prevLedger.dayZeroBudget, { seedTree: wipSeed });
                let totalTT = 0;
                const prevMap = {};
                Object.values(prevTree).forEach(node => {
                    calculateColumnTotals(node, prevLedger.dayZeroBudget);
                    totalTT += pNum(node.data?.totalTotal);
                });
                const walkMap = (obj) => {
                    Object.values(obj).forEach(row => {
                        prevMap[row.data?.breadcrumb] = isNaN(row.data?.totalTotal) ? 0 : pNum(row.data?.totalTotal);
                        if (row.rows && row.rows !== false) walkMap(row.rows);
                    });
                };
                walkMap(prevTree);
                setObjectMapPrev(prevMap);
                setPrevGrandTotal(totalTT);
            }
        }

        Promise.all(otherWeeks.map(async guid => {
            const result = await loadWeekVersion(guid);
            return { guid, ...result };
        })).then(results => {
            const maps = {};
            results.forEach(({ guid, map, grandTotal }) => { maps[guid] = { map, grandTotal }; });
            setWeekTotalMaps(maps);
            setWeekLoading({});
        });
    }, [varianceWeeks, selectedJob, drillColumns, userDataPrev, openCommitmentsPrev, costHistoryPrev, versions]);

    const collapse = (index) => { setCollapseNumber(index); setCollapseAll(Math.random().toString(16).substring(1)); };

    const deleteRow = (r) => {
        setDeletingRows(prev => new Set(prev).add(r.Forecast_Row_ID));
        setTimeout(() => {
            deleteUserData({ id: selectedJob, forecastGuid: selectedVersion, lockedVersion, rowId: r.Forecast_User_Data_GUID + '', tab: 'Other' });
            setUserData(prev => prev.filter(item => !(item.Forecast_Row_ID === r.Forecast_Row_ID && item.Forecast_Tab === 'Other')));
            setDeletingRows(prev => { const s = new Set(prev); s.delete(r.Forecast_Row_ID); return s; });
        }, 400);
    };

    const exportToExcel = async () => {
        try {
            setIsExporting(true);
            const ExcelJS = (await import('exceljs')).default;
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Other Data');
            const headers = ['', 'Current Budget', 'Actual Costs', 'Open Commitments', 'ETC', 'Total', 'Variance to Budget', 'Notes'];
            const headerRow = worksheet.addRow(headers);
            headerRow.font = { bold: true };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };

            const extractRows = (obj, level = 0) => {
                const rows = [];
                if (!obj) return rows;
                const r = obj.data;
                let title = r?.title || r?.key || '';
                rows.push({
                    title: '  '.repeat(level) + title,
                    budget: r?.path === 'Cost_Type_Code' && costTypeBudget ? (costTypeBudget[r.key] || '') : '',
                    actual: r?.actualTotal || '',
                    open: r?.openTotal || '',
                    etc: r?.etcTotal || '',
                    total: r?.totalTotal || '',
                    variance: r?.varianceBudget || '',
                    notes: r?.notes || '',
                });
                if (obj.rows && obj.rows !== false) {
                    Object.values(obj.rows).sort((a, b) => ('' + a?.data?.key).localeCompare(b?.data?.key)).forEach(child => {
                        rows.push(...extractRows(child, level + 1));
                    });
                }
                return rows;
            };

            Object.keys(objectMap).sort().forEach(key => {
                extractRows(objectMap[key]).forEach(row => {
                    worksheet.addRow([row.title, row.budget, row.actual, row.open, row.etc, row.total, row.variance, row.notes]);
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Other_${selectedJob}_${moment().format('YYYYMMDD')}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setIsExporting(false);
        }
    };

    // ── tabConfig for ThirdPartyRow ──
    const otherTabConfig = useMemo(() => ({
        tab: 'Other',
        defaultCostType: '',
        showCostTypeDropdown: false,
        costTypeDescriptions: [],
        stickyTitle: false,
        editableTitle: false,
        hasTCaretIndent: false,
        deferredSave: false,
        poDetailTitlePriority: 'remark',
        lineIdFallback: 'transaction',
        costCodeFallback: true,
        costTypeFromData: true,
        fixedCostType: null,
        marginLeftOffset: 42,
        collapsableChildPadding: 0,
        newPExtraFields: { notes: '', etc: '', costCode: '', maxGLDate: '', varianceBudget: '' },
        // ctBudget: costType → $ ; ctPhase: costType → phase → $ (wired in rowContextValue)
        budgetInit: (r, ctBudget, ctPhase) => (
            r?.path === 'Cost_Type_Code' && ctBudget ? ctBudget[r.key] ? fNum(ctBudget[r.key]) : ''
            : r?.path === 'Phase_Code' ? ctPhase && ctPhase[r.detail?.Cost_Type_Code] && ctPhase[r.detail?.Cost_Type_Code][r.key] ? fNum(ctPhase[r.detail?.Cost_Type_Code][r.key]) : ''
            : ''
        ),
        budgetDisplay: (props, r, currentBudget, userSaved) => {
            const budgetLevel = r?.path === 'Cost_Type_Code' || r?.path === 'Phase_Code';
            return !props.newRow && !r?.detail?.Forecast_Row_ID?.includes('~u') && budgetLevel ? currentBudget : '';
        },
        renderDetailColumns: (r, rowData, s, fNum, moment) => (
            <View style={{ flexDirection: 'row' }}>
                <Text style={s().notEdit}>{(r?.path === 'PO_Detail' || r?.path === 'Line_ID') && rowData?.OC ? moment(rowData?.Order_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{(r?.path === 'PO_Detail' || r?.path === 'Line_ID') && rowData?.OC ? moment(rowData?.Rental_Start_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{(r?.path === 'PO_Detail' || r?.path === 'Line_ID') && rowData?.OC ? moment(rowData?.Rental_End_Date).format('M/D/YYYY') : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? rowData?.Unit_Of_Measure_Description : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity_Received) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? fNum(rowData?.Ordered_Quantity_Open) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? fNum(rowData?.Unit_Price) : ''}</Text>
                <Text style={s().notEdit}>{r?.path === 'PO_Detail' || r?.path === 'Line_ID' ? fNum(rowData?.PO_Ordered_Amount) : ''}</Text>
            </View>
        ),
    }), []);

    const lookupOptions = useMemo(() => {
        const vendorSet = new Set();
        const poSet = new Set();
        [...openCommitments, ...costHistory].forEach(r => {
            if (r.Supplier_Name && r.Supplier_Name !== 'Unknown Vendor') vendorSet.add(r.Supplier_Name);
            if (r.PO_Number && r.PO_Number !== 'Unknown PO') poSet.add('' + r.PO_Number);
        });
        (userData || []).forEach(r => {
            if (r.Forecast_Tab === 'Other') {
                if (r.Supplier_Name) vendorSet.add(r.Supplier_Name);
                if (r.PO_Number) poSet.add('' + r.PO_Number);
            }
        });
        // Admin-uploaded Resource Code table filtered to this page's cost types.
        const resourceDropdown = buildResourceDropdownFromDb({
            byCode: dbResourceCodes.byCode || {},
            codesByCostType: dbResourceCodes.codesByCostType || {},
            allowedCostTypes: Object.keys(pageDescriptions),
            projectResources: {},
            budgetByResource: {},
            fallback: {},
        });
        return {
            phases: phaseDescriptions || {},
            costTypes: pageDescriptions,
            resources: resourceDropdown,
            vendors: [...vendorSet].sort(),
            pos: [...poSet].sort(),
            reverseDisciplineMap: {},
            disciplineMap,
            phaseResourceMap: {},
            budgetPhases: Object.keys(phaseDayZero || {}),
        };
    }, [phaseDescriptions, openCommitments, costHistory, userData, disciplineMap, phaseDayZero, dbResourceCodes, pageDescriptions]);

    const pivotPopoverProps = useMemo(() => ({
        drillColumns, tabConfig: otherTabConfig,
        selectedJob, selectedVersion, lockedVersion,
        userData, setUserData, lookupOptions,
    }), [drillColumns, otherTabConfig, selectedJob, selectedVersion, lockedVersion, userData, setUserData, lookupOptions]);

    const resourceDescriptions = useMemo(() => {
        const out = {};
        Object.entries(dbResourceCodes.byCode || {}).forEach(([c, info]) => {
            if (info?.description) out[c] = info.description;
        });
        return out;
    }, [dbResourceCodes]);

    const rowContextValue = useMemo(() => ({
        tabConfig: otherTabConfig,
        s, path, drillColumns,
        expandedBreadcrumbs, setExpandedBreadcrumbs,
        objectMapPrev, weekTotalMaps, varianceWeeks, weekDisplayMode,
        selectedWIP, descriptionVisible,
        // Other drills Cost_Type first, so budgetInit resolves cost-type budgets:
        // phaseDayZero slot → costTypeBudget (ct → $), phaseCCDayZero slot → budgetByCtPhase (ct → phase → $).
        phaseDayZero: costTypeBudget, phaseCCDayZero: budgetByCtPhase, phaseDescriptions, titleDescriptions, resourceDescriptions,
        selectedJob, selectedVersion, lockedVersion,
        userData, setUserData, focusedId, indentColors,
        collapseAll, collapseNumber, setCollapseNumber,
        entriesByBreadcrumb, setPopoverBreadcrumb, pivotPopoverProps,
        onOpenFuelChart: () => setShowFuelChart(true),
    }), [otherTabConfig, s, path, drillColumns, expandedBreadcrumbs, objectMapPrev, weekTotalMaps, varianceWeeks, weekDisplayMode, selectedWIP, descriptionVisible, costTypeBudget, budgetByCtPhase, phaseDescriptions, titleDescriptions, resourceDescriptions, selectedJob, selectedVersion, lockedVersion, userData, focusedId, collapseAll, collapseNumber, entriesByBreadcrumb, pivotPopoverProps, showFuelChart]);

    const sortedObjectMapKeys = useMemo(() => {
        if (!objectMap) return [];
        return Object.keys(objectMap).sort((a, b) => a ? (b ? a.localeCompare(b) : -1) : 1);
    }, [objectMap]);

    // ── Section grouping (same as other.js) ──
    const laborKeys = useMemo(() => sortedObjectMapKeys.filter(k => k === '51800'), [sortedObjectMapKeys]);
    const equipKeys = useMemo(() => sortedObjectMapKeys.filter(k => k === '52300' || k === '52400'), [sortedObjectMapKeys]);
    const otherKeys = useMemo(() => sortedObjectMapKeys.filter(k => k !== '51800' && k !== '52300' && k !== '52400'), [sortedObjectMapKeys]);

    const renderSection = (label, color, keys) => {
        if (!keys.length) return null;
        return (
            <View key={label} style={{ marginTop: 14, marginBottom: 4 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 5, paddingLeft: 18, paddingRight: 10, borderLeft: '4px solid ' + color, backgroundColor: color + '12', borderRadius: 3, marginBottom: 2 }}>
                    <Text style={{ fontSize: 12, fontWeight: '700', color: color, letterSpacing: 0.3 }}>{label}</Text>
                </View>
                {keys.map((phase) => (
                    <View key={phase} style={{ marginTop: 10 }}>
                        {phase?.includes('t^')
                            ? <ThirdPartyRow data={objectMap[phase]} />
                            : <PivotRowCollapsable data={objectMap[phase]} />
                        }
                    </View>
                ))}
            </View>
        );
    };

    // ── ValidationArea ──
    const levelColor = (lvl) => {
        // One series colour per level, in the fixed order (docs/STRIKE_STYLE.md, section 7)
        const colors = { 'Phase': SERIES[0], 'Vendor': SERIES[1], 'PO': SERIES[2], 'Line': SERIES[3], 'Cost Type': SERIES[4], 'User Row': SERIES_OTHER, 'Top Level': SERIES_OTHER };
        return colors[lvl] || SERIES_OTHER;
    };

    const descriptionText = (row) => [
        row.Cost_Code && row.Cost_Code !== 'undefined' && !row.Cost_Code.includes('^') ? (row.Cost_Code + (phaseDescriptions?.[row.Cost_Code] ? ' - ' + phaseDescriptions[row.Cost_Code] : '')) : null,
        row.Supplier_Name && row.Supplier_Name !== 'undefined' && !row.Supplier_Name.includes('^') ? row.Supplier_Name : null,
        row.PO_Number && row.PO_Number !== 'undefined' && !(row.PO_Number + '').includes('^') ? row.PO_Number : null,
    ].filter(Boolean).join(' \u203A ');

    const getRowLevel = (row) => {
        if (row.Equipment_Tier && row.Equipment_Tier !== '' && row.Equipment_Tier !== 'undefined') return row.Equipment_Tier;
        if (row.Forecast_Row_ID?.includes('^~u')) return 'User Row';
        if (row.Forecast_Row_ID?.includes('t^')) return 'Top Level';
        const colMap = { 'Phase_Code': row.Cost_Code, 'Supplier_Name': row.Supplier_Name, 'PO_Number': row.PO_Number, 'Cost_Type_Code': row.Cost_Type, 'Line_ID': '' };
        const labelMap = { 'Phase_Code': 'Phase', 'Supplier_Name': 'Vendor', 'PO_Number': 'PO', 'Line_ID': 'Line', 'Cost_Type_Code': 'Cost Type' };
        for (let i = drillColumns.length - 1; i >= 0; i--) {
            const val = colMap[drillColumns[i]];
            if (val && val !== 'undefined' && !val.includes('^')) return labelMap[drillColumns[i]] || drillColumns[i];
        }
        return 'Cost Type';
    };

    const ValidationArea = () => {
        const totalAllRows = (sortedUserData || [])
            .filter(row => row.Forecast_Tab === 'Other' && row.Value_Amount)
            .reduce((sum, row) => sum + pNum(row.Value_Amount), 0);

        const cols = [
            { label: 'Code', w: 70 },
            { label: 'Title', w: 110 },
            { label: 'Path', w: 180 },
            { label: 'Description', w: 140 },
            { label: 'Notes', w: 130 },
            { label: 'ETC', w: 110, align: 'right' },
            { label: '', w: 40 },
        ];

        const headerRow = (cols) => (
            <View style={{ flexDirection: 'row', backgroundColor: '#1e293b', paddingHorizontal: 4, paddingLeft: 24, height: 18, alignItems: 'center' }}>
                {cols.map((col, i) => (
                    <Text key={i} style={{ width: col.w, fontSize: 11, fontWeight: '600', color: '#f1f5f9', textAlign: col.align || 'left', paddingHorizontal: 8 }}>{col.label}</Text>
                ))}
            </View>
        );

        const rowBreadcrumb = (row) => {
            const colMap = { Phase_Code: row.Cost_Code || '', Supplier_Name: row.Supplier_Name || '', PO_Number: row.PO_Number || '', Cost_Type_Code: row.Cost_Type || '', Discipline: '', Resource_Code: row.Resource_Code || '', Line_ID: '' };
            let bc = '';
            for (let i = 0; i < drillColumns.length; i++) {
                const v = colMap[drillColumns[i]] || '';
                if (!v || v === 'undefined' || v.includes('^')) break;
                bc += '^' + v;
            }
            return bc;
        };

        const dataRow = (row, idx) => {
            const bc = rowBreadcrumb(row);
            return (
                <View key={row.Forecast_User_Data_GUID || row.Forecast_Row_ID} style={{
                    flexDirection: 'row', alignItems: 'center', paddingHorizontal: 4, paddingLeft: 24, height: 28,
                    backgroundColor: deletingRows.has(row.Forecast_Row_ID) ? '#fee2e2' : (idx % 2 === 0 ? '#ffffff' : '#f8fafc'),
                    borderBottom: '1px solid #f1f5f9',
                    opacity: deletingRows.has(row.Forecast_Row_ID) ? 0.5 : 1,
                    transition: 'opacity 0.3s, background-color 0.3s',
                }}>
                    <View style={{ width: 70, paddingHorizontal: 8 }}>
                        <Text style={{ fontSize: 12, color: '#475569', fontFamily: 'monospace' }}>{row.Cost_Type}</Text>
                        <Text style={{ fontSize: 9, color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} numberOfLines={1}>{tDescriptions[row.Cost_Type] || ''}</Text>
                    </View>
                    <Text style={{ width: 110, fontSize: 12, color: '#1e293b', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Row_Label || ''}</Text>
                    <View style={{ width: 180, paddingHorizontal: 8, flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                        <Text style={{ fontSize: 12, color: '#1e293b', lineHeight: 18, flex: 1 }} numberOfLines={1}>{descriptionText(row)}</Text>
                    </View>
                    <Text style={{ width: 140, fontSize: 12, color: '#1e293b', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Description || ''}</Text>
                    <Text style={{ width: 130, fontSize: 12, color: '#475569', paddingHorizontal: 8, lineHeight: 18 }} numberOfLines={1}>{row.Notes || ''}</Text>
                    <Text style={{ width: 110, fontSize: 12, fontWeight: '500', color: '#1e293b', textAlign: 'right', paddingHorizontal: 8, fontFamily: 'monospace' }}>{fNum(row.Value_Amount)}</Text>
                    <View style={{ width: 40, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                        <Pressable style={{ cursor: 'pointer' }} onPress={() => setPopoverBreadcrumb(bc)}>
                            <Icon3 name="MdEdit" color="#3b82f6" size={14} />
                        </Pressable>
                        <Pressable style={{ cursor: deletingRows.has(row.Forecast_Row_ID) ? 'default' : 'pointer' }}
                            onPress={() => !deletingRows.has(row.Forecast_Row_ID) && deleteRow(row)}>
                            {deletingRows.has(row.Forecast_Row_ID)
                                ? <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(0,0,0,0.1)', borderTop: '2px solid #dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                : <Icon3 name="MdDeleteOutline" color="#dc2626" size={14} />}
                        </Pressable>
                    </View>
                </View>
            );
        };

        const flatRows = (sortedUserData || []).filter(row => row.Forecast_Tab === 'Other' && row.Value_Amount);

        return (
            <View style={{ flexDirection: 'row', marginTop: 16 }}>
                <View style={{ backgroundColor: '#ffffff', borderRadius: 8, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: 820 }}>
                    <View style={{ padding: 10, borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#0f172a' }}>ETC Values Summary</Text>
                    </View>
                    {headerRow(cols)}
                    {flatRows.map((row, idx) => dataRow(row, idx))}
                    <View style={{ flexDirection: 'row', alignItems: 'center', paddingHorizontal: 4, paddingLeft: 24, height: 18, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                        <Text style={{ width: 630, fontSize: 12, fontWeight: '600', color: '#334155', paddingHorizontal: 8 }}>Total</Text>
                        <Text style={{ width: 110, fontSize: 13, fontWeight: '700', color: '#1d4ed8', textAlign: 'right', paddingHorizontal: 8, fontFamily: 'monospace' }}>{fNum(totalAllRows)}</Text>
                        <View style={{ width: 40 }} />
                    </View>
                </View>
                {/* Incentive Eligible box hidden for now — flip `false` to re-enable */}
                {false && <View style={{ marginLeft: 20, backgroundColor: 'white', borderRadius: 8, padding: 20, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.08, shadowRadius: 6, elevation: 3, border: '1px solid #e5e7eb' }}>
                    <Text style={{ textAlign: 'right', fontSize: 14, fontWeight: 500, marginBottom: 10 }}>Incentive Eligible</Text>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={{ flex: 1, textAlign: 'left', fontSize: 14 }}>Eligible</Text>
                        <Text style={{ flex: 1, textAlign: 'right', fontSize: 14 }}>{incentiveEligible}</Text>
                    </View>
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={{ flex: 1, textAlign: 'left', fontSize: 14 }}>{incentiveEligible === 'Yes' ? 'Eligible Amount' : ''}</Text>
                        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                            <Text style={{ fontSize: 14 }}>{incentiveEligibleAmount}</Text>
                            {/* No add button once Field Bonus is retired — it would write an invisible 51800 row */}
                            {incentiveEligibleAmount && !hideFieldBonus ? (
                                <Pressable
                                    onPress={() => {
                                        const amount = pNum(incentiveEligibleAmount);
                                        if (!amount) return;
                                        const newRowId = '^51800^~u' + Math.random().toString(16).substring(1);
                                        updateUserDataArray({
                                            active: 1,
                                            userId: '154045',
                                            userName: 'Seth Bailey',
                                            fieldName: 'etc',
                                            tab: 'Other',
                                            rowId: newRowId,
                                            version: selectedVersion + '',
                                            value: amount + '',
                                            id: selectedJob,
                                            costType: '51800',
                                            costCode: '',
                                            vendor: '',
                                            po: '',
                                            equipment: 'Cost Type',
                                            lockedVersion: lockedVersion,
                                            valueAmount: amount,
                                            description: '',
                                            notes: 'From incentive calc',
                                        });
                                        setUserData(prev => ([...(prev || []), {
                                            Forecast_Field_Name: 'etc',
                                            Forecast_Value: amount + '',
                                            Forecast_Row_ID: newRowId,
                                            Forecast_Tab: 'Other',
                                            Forecast_User_Data_GUID: '',
                                            Phase_Code: '',
                                            Supplier_Name: '',
                                            PO_Number: '',
                                            Equipment_Tier: 'Cost Type',
                                            BU_ID: selectedJob,
                                            Cost_Code: '',
                                            Cost_Type: '51800',
                                            Value_Amount: amount,
                                            Notes: 'From incentive calc',
                                            Description: '',
                                        }]));
                                    }}
                                    style={{ cursor: 'pointer', padding: 2 }}
                                >
                                    <Icon3 name="MdAddCircleOutline" color="#1d4ed8" size={16} />
                                </Pressable>
                            ) : null}
                        </View>
                    </View>
                    {incentiveEligible === 'Yes' && incentiveCalcBreakdown ? <View style={{ flexDirection: 'row', marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Text style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>Calc: </Text>
                        <View style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: '#ccc', marginHorizontal: 1 }}>
                            <TextInput style={{ textAlign: 'center', fontSize: 11, fontStyle: 'italic', color: '#444', width: 24, height: 16, padding: 0, borderWidth: 0, backgroundColor: 'transparent' }}
                                value={incentiveRate}
                                onChangeText={(a) => {
                                    setIncentiveRate(a);
                                    updateUserDataArray({
                                        userId: '154045',
                                        userName: 'Seth Bailey',
                                        tab: 'Forecast',
                                        rowId: 1,
                                        version: selectedVersion + '',
                                        id: selectedJob,
                                        lockedVersion: lockedVersion,
                                        fieldName: 'incentiveRate',
                                        value: a + ''
                                    });
                                }} />
                            <Text style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}>%</Text>
                        </View>
                        <Text style={{ fontSize: 11, color: '#666', fontStyle: 'italic' }}> {incentiveCalcBreakdown.split('%').slice(1).join('%')}</Text>
                    </View> : null}
                    <View style={{ flexDirection: 'row' }}>
                        <Text style={{ flex: 1, textAlign: 'left', fontSize: 11, color: '#999', marginTop: 4 }}>{incentiveEligible === 'Yes' ? '*only calculates if field bonus ETC is zero' : ''}</Text>
                    </View>
                </View>}
            </View>
        );
    };

    // ── RENDER ──
    return (
        <View style={{ margin: 30, marginTop: 0, marginRight: 200, marginBottom: 200 }}>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

            <View style={{ marginBottom: 8, flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <PivotSelector columns={drillColumns} available={availablePivotColumns} onChange={setDrillColumns} />
                <VarianceWeekSelector selectedWeeks={varianceWeeks} onChange={setVarianceWeeks} />
                <ReversedVoucherLegend tree={objectMap} />
            </View>

            <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 20, paddingTop: 0, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3, position: 'relative' }}>
                {rebuilding && (
                    <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 200, alignItems: 'center', justifyContent: 'center' }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: 'white', padding: '8px 16px', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                            <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                            <Text style={{ fontSize: 12, fontWeight: '600', color: '#6b7280' }}>Rebuilding tree…</Text>
                        </View>
                    </View>
                )}
                <View style={{ backgroundColor: 'white', position: 'sticky', top: 0, zIndex: 100, paddingTop: 20, width: 'max-content', minWidth: '100%' }}>
                    <View style={{ flexDirection: 'row', marginLeft: 22 }}>
                        <View style={[s(300).title, { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }]}>
                            <Pressable
                                style={{ flexDirection: 'row', alignItems: 'center', gap: 3, padding: '2px 6px', borderRadius: 3, border: '1px solid #d1d5db', cursor: 'pointer', backgroundColor: '#f9fafb' }}
                                onPress={() => collapse(1)}>
                                <Icon3 name="MdUnfoldLess" size={11} color="#6b7280" />
                                <Text style={{ color: '#6b7280', fontSize: 11, fontWeight: '600' }}>Collapse</Text>
                            </Pressable>
                        </View>
                        <View style={[s(140).title, { flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 5 }]}>
                            <Text style={{ fontSize: 12 }}>Description</Text>
                            <Icon2
                                onPress={() => setDescriptionVisible(!descriptionVisible)}
                                name={descriptionVisible ? 'chevron-down-circle-outline' : 'chevron-forward-circle-outline'}
                                size={16}
                            />
                        </View>
                        {descriptionVisible ? <View style={{ flexDirection: 'row' }}>
                            <Text style={s().title}>Order Date</Text>
                            <Text style={s().title}>Start Date</Text>
                            <Text style={s().title}>End Date</Text>
                            <Text style={s().title}>UoM</Text>
                            <Text style={s().title}>Ordered</Text>
                            <Text style={s().title}>Received</Text>
                            <Text style={s().title}>Open</Text>
                            <Text style={s().title}>Unit Price</Text>
                            <Text style={s().title}>PO Amount</Text>
                        </View> : null}
                        <View style={{ width: 10 }} />
                        <Text style={s().title}>Current Budget</Text>
                        <View style={{ width: 10 }} />
                        <Text style={s().title}>Most Recent Cost Date</Text>
                        <Text style={s().title}>Actual Costs</Text>
                        <Text style={s().title}>Open</Text>
                        <Text style={s(120).title}>Estimate to Complete (ETC)</Text>
                        <Text style={s().title}>Total</Text>
                        <Text style={s().title}>Variance to Budget (+)/-</Text>
                        {varianceWeeks.map(guid => (
                            <View key={guid} style={[s().title, { backgroundColor: '#eef2ff', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }]}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                                    <Text style={{ color: '#4338ca', fontSize: 12, fontWeight: '700' }}>{getWeekLabel(guid)}</Text>
                                    {weekLoading[guid] ? <Text style={{ fontSize: 10, color: '#818cf8', animation: 'spin 1s linear infinite' }}>⟳</Text> : null}
                                </View>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                    <Pressable onPress={() => setWeekDisplayMode(prev => ({ ...prev, [guid]: 'total' }))} style={{ cursor: 'pointer', backgroundColor: (weekDisplayMode[guid] || 'var') === 'total' ? '#4338ca' : '#e0e7ff', borderRadius: 3, padding: '1px 4px' }}>
                                        <Text style={{ fontSize: 8, fontWeight: '700', color: (weekDisplayMode[guid] || 'var') === 'total' ? 'white' : '#6366f1' }}>TOT</Text>
                                    </Pressable>
                                    <Pressable onPress={() => setWeekDisplayMode(prev => ({ ...prev, [guid]: 'var' }))} style={{ cursor: 'pointer', backgroundColor: (weekDisplayMode[guid] || 'var') === 'var' ? '#4338ca' : '#e0e7ff', borderRadius: 3, padding: '1px 4px' }}>
                                        <Text style={{ fontSize: 8, fontWeight: '700', color: (weekDisplayMode[guid] || 'var') === 'var' ? 'white' : '#6366f1' }}>VAR</Text>
                                    </Pressable>
                                </View>
                            </View>
                        ))}
                        <View style={{ flexDirection: 'row' }}>
                            <Text style={s(400).title}>Notes</Text>
                            <Pressable onPress={() => !isExporting && exportToExcel()}>
                                <Text style={{ backgroundColor: isExporting ? '#c0c0c0' : '#4CAF50', color: 'white', borderRadius: 2, textAlign: 'center', padding: 5, margin: 2, fontSize: 12 }}>{isExporting ? 'EXPORTING...' : 'EXPORT'}</Text>
                            </Pressable>
                        </View>
                    </View>

                    <View style={{ flexDirection: 'row', marginLeft: 21 }}>
                        <Text style={[s(descriptionVisible ? 1341 : 441).notEdit, { textAlign: 'left', backgroundColor: '#f0f0f0' }]}>Totals</Text>
                        <View style={{ width: 10, backgroundColor: 'white' }} />
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{totalCurrentBudget}</Text>
                        <View style={{ width: 110, backgroundColor: 'white' }} />
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{actualTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{openTotalTotal}</Text>
                        <Text style={[s(120).notEdit, { backgroundColor: '#f0f0f0' }]}>{etcTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{totalTotalTotal}</Text>
                        <Text style={[s().notEdit, { backgroundColor: '#f0f0f0' }]}>{varianceBudgetTotal}</Text>
                        {varianceWeeks.map(guid => {
                            const gt = guid === selectedWIP?.Version_GUID ? prevGrandTotal : weekTotalMaps[guid]?.grandTotal;
                            const mode = weekDisplayMode[guid] || 'var';
                            const displayGt = mode === 'var' && gt != null ? pNum(totalTotalTotal) - pNum(gt) : gt;
                            return (
                                <Text key={guid} style={[s().notEdit, { backgroundColor: mode === 'var' ? '#fef9ee' : '#eef2ff' }]}>
                                    {displayGt != null ? fNum(displayGt) : ''}
                                </Text>
                            );
                        })}
                    </View>
                </View>

                <ThirdPartyRowContext.Provider value={rowContextValue}>
                    <PopoverBreadcrumbContext.Provider value={popoverBreadcrumb}>
                        <FuelETCWarningModal open={showFuelChart} onClose={() => setShowFuelChart(false)} jobCostDetail={jobCostHistory} userData={userData} openCommitments={jobOpenCommitments} />
                        <View style={{ marginTop: 1 }}>
                            {renderSection('LABOR', '#3b82f6', laborKeys)}
                            {renderSection('EQUIPMENT', '#f59e0b', equipKeys)}
                            {renderSection('OTHER', '#6b7280', otherKeys)}
                        </View>
                        <EtcPopoverHost
                            objectMap={objectMap}
                            popoverBreadcrumb={popoverBreadcrumb}
                            setPopoverBreadcrumb={setPopoverBreadcrumb}
                            entriesByBreadcrumb={entriesByBreadcrumb}
                            pivotPopoverProps={pivotPopoverProps}
                        />
                    </PopoverBreadcrumbContext.Provider>
                </ThirdPartyRowContext.Provider>
            </View>

            <ValidationArea />
            <AuditHistoryPanel tabKey="other" />
        </View>
    );
};

export default Other;
